<template>
    <b-modal id="rule-group-detail-modal"
             :title="modalTitle"
             hide-footer
             size="xl"
             modal-class="default-modal"
             @hide="closeModal"
    >
        <div class="action-btns">
            <template v-if="mode === 'readOnly'">
                <div style="height: 32px"/>
            </template>
            <template v-else-if="mode === 'detail'">
                <b-button variant="outline-primary"
                          :disabled="!detailData.id"
                          @click="changeMode('edit')"
                >
                    {{ $t('수정')}}
                </b-button>
            </template>
            <template v-else-if="mode === 'add'">
                <b-button variant="outline-primary"
                          form="rule-group-form"
                          type="submit"
                >
                    {{ $t('저장')}}
                </b-button>
            </template>
            <template v-else-if="mode === 'edit'">
                <b-button variant="outline-danger"
                          @click="confirmDelete"
                >
                    {{ $t('삭제') }}
                </b-button>
                <b-button variant="outline-primary"
                          form="rule-group-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
                <b-button variant="outline-secondary"
                          @click="changeMode('detail')"
                >
                    {{ $t('취소') }}
                </b-button>
            </template>
        </div>
        <div class="tabs-container">
            <b-tabs v-model="tabIndex" class="default-tabs" :class="{'with-actions': mode !== 'add'}" content-class="mt-3">
                <b-tab :title="$t('기본 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <PageEmptyComponent v-else-if="loadError && mode !== 'add'" compact :error="loadError" @retry="fetchDetailData"/>
                    <template v-else>
                        <RuleGroupDetail v-if="mode === 'detail' || mode === 'readOnly'" :detail-data="detailData"
                                         :parent-data="parentData"
                        />
                        
                        <RuleGroupForm v-else ref="ruleGroupForm" :mode="mode" :detail-data="detailData"
                                       :parent-data="parentData"
                                     @updateDetailData="updateDetailData"
                        />
                    </template>
                </b-tab>
            </b-tabs>
        </div>
    </b-modal>
</template>

<script>
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {deleteRuleGroup, getRuleGroup} from '@shared_utils/api/ruleEngine';

import RuleGroupDetail
    from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/RuleGroupDetail.vue';
import RuleGroupForm from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/RuleGroupForm.vue';

export default {
    name: 'RuleGroupModal',
    components: {
        PageEmptyComponent,
        RuleGroupForm,
        RuleGroupDetail,
    },
    props: {
        detailId: {
            type: [Number, String, null],
            default: null,
        }
    },
    data() {
        return {
            mode: 'detail', // detail | add | edit | readOnly
            tabIndex: 1,
            detailData: {id: null},
            parentData: null,
            loading: false,
            loadError: '',
        };
    },
    computed: {
        modalTitle() {
            if (this.mode === 'add') {
                return this.$t('규칙 그룹 등록');
            } else if (this.mode === 'edit') {
                return this.$t('규칙 그룹 수정');
            } else {
                return this.$t('규칙 그룹 상세');
            }
        },
        readOnly(){
            return this.mode === 'readOnly';
        }
    },
    watch: {
        detailId: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchDetailData();
                } else {
                    this.detailData = {id: null};
                }
            }
        }
    },
    methods: {
        changeMode(mode) {
            this.mode = mode;
            this.tabIndex = 1;
        },
        closeModal() {
            if (this.mode === 'edit') {
                this.$refs.ruleGroupForm.resetForm();
            }
            this.mode = 'detail';
            this.$bvModal.hide('rule-group-detail-modal');
        },
        async fetchDetailData(){
            try {
                this.loading = true;
                this.loadError = '';
                this.detailData = {id: null};
                const res = await getRuleGroup(this.detailId);
                this.detailData = res.data;
                if(this.detailData.parent_id != null){
                    const res = await getRuleGroup(this.detailData.parent_id);
                    this.parentData = res.data;
                }
            }
            catch (e) {
                console.error(e);
                this.loadError = e.response && e.response.status === 404
                    ? '존재하지 않는 항목입니다.'
                    : getErrorMessage(e, '상세 정보를 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', this.$t('상세 조회 실패'), this.loadError);
            }
            finally {
                this.loading = false;
            }
        },
        updateDetailData(detailData) {
            if(this.mode === 'add'){
                this.$emit('addData', detailData);
                this.closeModal();
            }
            else if(this.mode === 'edit'){
                this.$emit('updateDetailData', detailData);
                this.detailData = detailData;
                this.changeMode('detail');
            }
        },
        confirmDelete(){
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('규칙 그룹 삭제'),
                message: this.$t('현 규칙 그룹을 삭제하시겠습니까?')
            })
                .then((v)=>{
                    if(v){
                        this.deleteRuleGroup();
                    }
                });
        },
        deleteRuleGroup(){
            deleteRuleGroup(this.detailData.id)
                .then(() => {
                    this.$EventBus.$emit('toast-success', this.$t('규칙 그룹 삭제'), this.$t('규칙 그룹이 성공적으로 삭제되었습니다.'));
                    this.$emit('deleteData', this.detailData.id);
                    this.closeModal();
                })
                .catch((error) => {
                    console.error(error);
                    if(error?.response.status === 400){
                        const errorMsg = getErrorMessage(error, '');
                        console.log(errorMsg,errorMsg?.includes('Cannot delete rule group. It is used by campaign rule sets:'));
                        if(errorMsg?.includes('Cannot delete rule group. It is used by campaign rule sets:')){
                            const match = errorMsg.match(/\[([^\]]*)\]/);

                            if (match) {
                                const h = this.$createElement;
                                const campaignRuleSet = match[1].replace(/'/g, '').split(', ');
                                const badges = campaignRuleSet.map(name => {
                                    return h(
                                        'b-badge',
                                        {
                                            props: { variant: 'warning' },
                                            class: 'mx-1 f-90'
                                        },
                                        name
                                    );
                                });

                                const messageVNode = h(
                                    'div', 
                                    [
                                        h('p', '해당 규칙 그룹은 아래 캠페인 규칙셋에서 사용 중이므로 삭제할 수 없습니다.'),
                                        h('div', { class: 'mt-3' }, badges) 
                                    ]
                                );

                                this.$showModal({
                                    type: 'alert',
                                    state: 'error',
                                    title: this.$t('규칙 그룹 삭제 오류'),
                                    message: messageVNode 
                                });
                            }
                            return;
                        }
                    }
                    this.$EventBus.$emit('toast-failure', this.$t('규칙 그룹 삭제'), this.$t('규칙 그룹 삭제 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
                });
        }
    },
};
</script>

<style scoped lang="scss">

</style>
